import React from 'react'
import {Box, Button, Flex, Text} from '../../../../utils/chakraMonomorphic'

import {
  FaceOffGoldenPhoto,
  FaceOffGoldenSummary,
  FaceOffRemoval,
} from '../../../../services/coreApi/faceoff'
import copy from './copy.json'
import {fillCopy} from './constants'

// One queue entry's identity: a photo id, or photo and person for a case.
const removalId = (r: FaceOffRemoval): string =>
  r.personId ? `${r.photoId}:${r.personId}` : String(r.photoId)

export interface RemovalQueue {
  removals: FaceOffRemoval[]
  isQueued: (r: FaceOffRemoval) => boolean
  toggle: (r: FaceOffRemoval) => void
  clear: () => void
}

/**
 * Removals waiting to be applied. They write nothing until Apply sends them
 * all as one golden version. A new `version` empties the queue.
 */
export const useRemovalQueue = (version: number): RemovalQueue => {
  const [removals, setRemovals] = React.useState<FaceOffRemoval[]>([])
  React.useEffect(() => setRemovals([]), [version])
  return {
    removals,
    isQueued: (r): boolean =>
      removals.some((x) => removalId(x) === removalId(r)),
    toggle: (r): void =>
      setRemovals((q) =>
        q.some((x) => removalId(x) === removalId(r))
          ? q.filter((x) => removalId(x) !== removalId(r))
          : [...q, r],
      ),
    clear: (): void => setRemovals([]),
  }
}

/** What the set holds once the queue is applied. */
export const afterRemovals = (
  photos: FaceOffGoldenPhoto[],
  removals: FaceOffRemoval[],
): {cases: number; photos: number; gone: number} => {
  const ids = new Set(removals.map(removalId))
  let cases = 0
  let gone = 0
  const kept = new Set<number>()
  photos.forEach((p) =>
    p.cases.forEach((c) => {
      if (ids.has(String(p.photoId)) || ids.has(`${p.photoId}:${c.personId}`)) {
        gone += 1
      } else {
        cases += 1
        kept.add(p.photoId)
      }
    }),
  )
  return {cases, photos: kept.size, gone}
}

export const QueueCaseButton: React.FC<{
  queue: RemovalQueue
  photoId: number
  personId: number
}> = ({queue, photoId, personId}) => {
  const queued = queue.isQueued({photoId, personId})
  return (
    <Button
      size='xs'
      variant={queued ? 'solid' : 'outline'}
      colorScheme={queued ? 'red' : undefined}
      onClick={(): void => queue.toggle({photoId, personId})}
      data-testid='face-off-golden-queue-case'>
      {queued ? copy.golden.unqueueCase : copy.golden.queueCase}
    </Button>
  )
}

export const QueuePhotoButton: React.FC<{
  queue: RemovalQueue
  photo: FaceOffGoldenPhoto
}> = ({queue, photo}) => {
  const queued = queue.isQueued({photoId: photo.photoId})
  let text = fillCopy(copy.golden.unqueuePhoto, {photoId: photo.photoId})
  if (!queued)
    text = fillCopy(
      photo.cases.length === 1
        ? copy.golden.queuePhotoOne
        : copy.golden.queuePhoto,
      {photoId: photo.photoId, cases: photo.cases.length},
    )
  return (
    <Button
      size='xs'
      variant={queued ? 'solid' : 'outline'}
      colorScheme='red'
      onClick={(): void => queue.toggle({photoId: photo.photoId})}
      data-testid='face-off-golden-queue-photo'>
      {text}
    </Button>
  )
}

/** Apply and Clear, with what is queued and what the set keeps after. */
export const RemovalQueueBar: React.FC<{
  queue: RemovalQueue
  photos: FaceOffGoldenPhoto[]
  onApply: () => void
}> = ({queue, photos, onApply}) => {
  const {removals} = queue
  const left = afterRemovals(photos, removals)
  const items = removals
    .map((r) =>
      r.personId
        ? fillCopy(copy.golden.queuedCase, {
            photoId: r.photoId,
            personId: r.personId,
          })
        : fillCopy(copy.golden.queuedPhoto, {photoId: r.photoId}),
    )
    .join(', ')
  return (
    <Flex
      gap={2}
      alignItems='center'
      flexWrap='wrap'
      data-testid='face-off-golden-queue'>
      <Button
        size='sm'
        colorScheme='red'
        isDisabled={removals.length === 0}
        onClick={onApply}
        data-testid='face-off-golden-apply'>
        {removals.length === 1
          ? copy.golden.applyOne
          : fillCopy(copy.golden.apply, {count: removals.length})}
      </Button>
      <Button
        size='sm'
        variant='outline'
        isDisabled={removals.length === 0}
        onClick={queue.clear}
        data-testid='face-off-golden-clear'>
        {copy.golden.clear}
      </Button>
      {removals.length > 0 && (
        <Text fontSize='sm' data-testid='face-off-golden-queued'>
          {fillCopy(copy.golden.queued, {
            items,
            cases: left.cases,
            photos: left.photos,
          })}
        </Text>
      )}
    </Flex>
  )
}

/**
 * A golden version's details, as Face Off's header lists them: what it
 * extends, where it came from, who signed off its cases, its note and the
 * newest run's pass rate.
 */
export const GoldenDetails: React.FC<{
  summary: FaceOffGoldenSummary
}> = ({summary}) => {
  const d = copy.golden.details
  const rows: [string, string][] = [
    [
      d.goldenSet,
      summary.extends === null
        ? fillCopy(d.goldenSetValue, {version: summary.version})
        : fillCopy(d.extendsValue, {
            version: summary.version,
            extends: summary.extends,
          }),
    ],
    [d.created, summary.createdUtc],
    [d.source, summary.source ?? d.notRecorded],
    [
      d.cases,
      fillCopy(d.casesValue, {cases: summary.cases, photos: summary.photos}),
    ],
    [
      d.signedOff,
      summary.signOffs.length === 0
        ? d.notRecorded
        : summary.signOffs
            .map((s) => fillCopy(d.signOff, {by: s.by, at: s.at}))
            .join(', '),
    ],
    [d.note, summary.note ?? d.none],
    [
      d.lastPassRate,
      summary.lastPassRate === null
        ? d.noRun
        : `${(summary.lastPassRate * 100).toFixed(1)}%`,
    ],
  ]
  return (
    <Box
      as='dl'
      display='grid'
      gridTemplateColumns='max-content 1fr'
      columnGap={4}
      rowGap={1}
      fontSize='sm'
      data-testid='face-off-golden-details'>
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <Box as='dt' color='var(--fo-muted)'>
            {label}
          </Box>
          <Box as='dd' data-testid='face-off-golden-detail'>
            {value}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  )
}
